import SidePanelHeader from "../components/SidePanelHeader"
import SidePanelBreadCrumb from "../components/SidePanelBreadCrumb"
import SidePanelAuditTrail from "../components/SidePanelAuditTrail"
import { EntityNames } from "../../../../../enums/EntityNames"
import { useNavigate } from "react-router"
import * as Navigation from "../../../../../modules/Navigation";
import SidePanelInfo from "../components/SidePanelInfo"
import SidePanelNonSensitiveInfo from "../components/SidePanelNonSensitiveInfo"
import { httpGet, httpPost } from "../../../../../modules/Backend"
import { useEffect, useState } from "react"
import { hasPermission } from "../../../../../modules/auth/Permissions/PermissionModule";
import { PermissionConcerns, PermissionTypes } from "../../../../../modules/auth/Permissions/PermissionStrings";
import { usePermissions } from "../../../../../modules/auth/Permissions/PermissionContext"
import { OuterCubeSchema } from "../../../entityComponents/Schemas/OuterCubeSchema"
import { hasVisibleChildren } from "../../../entityComponents/Schema"
import moveIcon from "../../../../../assets/icons/icon-move-white.svg"
import { Button } from "react-aria-components"
import MoveModal from "../../../Modal/MoveModal"
import { modalSignal } from "../../../Modal/ModalContainer"
import PrintOuterCubeLabel from "../../../print/PrintOuterCubeLabel"
import { globalSubmittingSignal, headerWidgetSignal } from "../../Layout"
import { useSignals } from "@preact/signals-react/runtime"
import Percentage from "../../../Percentage"
import SidePanelOuterCubeMaterial from "../components/SidePanelOuterCubeMaterial"
import FullPartFullModal from "../../../Modal/FullPartFullModal"
import { HttpStatusCode } from "axios"
import { getStatus } from "../../../../../enums/Domain/StatusLookup"
import ActionBar from "../actions/ActionBar"
import EditAction from "../actions/EditAction"
import { OuterCubeTypeSmartEnum } from "../../../../../enums/Domain/OuterCubeTypeSmartEnum"
import ConfirmModal, { ConfirmType } from "../../../Modal/ConfirmModal"

export default function SidePanelOuterCubes({ data, onViewPage }: any) {
    useSignals();
    const navigate = useNavigate()
    const [location, setLocation] = useState<string>("")
    const [fullness, setFullness] = useState<string>(data.fullness)
    const permissionsContext = usePermissions();

    function viewEntity() {
        // Only navigate if there are visible children
        if (hasVisibleChildren(data.entityName, permissionsContext)) {
            Navigation.view.navigate(navigate, data.entityName, data.id, data)
        }
    }

    function viewEntityAndTab(tabName?: string) {
        // Only navigate if there are visible children
        if (hasVisibleChildren(data.entityName, permissionsContext)) {
            var indexOfTab = OuterCubeSchema.children?.findIndex(x => x.entityName == tabName) ?? 0
            Navigation.view.navigate(navigate, data.entityName, data.id, data, indexOfTab)
        }
    }

    async function getLocation() {
        const result = await httpGet(`api/outerCubes/location?id=${data.id}`)
        if (result?.data?.breadcrumb) {
            setLocation(result.data.breadcrumb)
        }
    }

    function handlePartFullOrFull() {
        modalSignal.value = <FullPartFullModal
            heading="Mark as part full or full?"
            information="If this outer cube is currently at a workstation, it will be moved to 'Packing Area Out'"
            ContinueFunc={markAsPartFullOrFull} />
    }

    async function markAsPartFullOrFull(isFull: boolean) {
        const payload = {
            id: data.id,
            isFull
        }
        const result = await httpPost("api/outerCubes/markAsFullPartFull", payload);
        if (result.status === HttpStatusCode.Ok) {
            if (result.data.workstationLocationId) {
                Navigation.view.navigate(navigate, EntityNames.workstations, result.data.workstationLocationId);
            } else {
                setFullness(result.data.fullness);
            }
        }
    }

    function handleMove() {
        modalSignal.value = <MoveModal entitiesToMove={[data]} entityName={EntityNames.outerCubes} onMoved={(newLocation: string) => setLocation(newLocation)} />
    }

    async function deleteOuterCube() {
        const payload = { id: data.id };
        const result = await httpPost("api/outerCubes/delete", payload);
        if (result.status === HttpStatusCode.Ok) {
            if (onViewPage) {
                navigate(-1);
            } else {
                window.location.reload();
            }
        }
    }

    function handleDeleteOuterCube() {
        modalSignal.value = <ConfirmModal heading="Delete pallet?"
            information="This action cannot be undone"
            ContinueFunc={deleteOuterCube}
            type={ConfirmType.Destructive} />
    }

    function getOuterCubeTypeForDisplay() {
        if (data?.type === null || data?.type === undefined) {
            return '';
        }
        const status = getStatus(data.type);
        return status?.displayValue;
    }

    useEffect(() => {
        headerWidgetSignal.value = data.percentageFull !== null && data.percentageFull !== undefined
            ? <OuterCubeFullness amount={data.percentageFull} />
            : undefined

        data.currentLocation ? setLocation(data.currentLocation) : getLocation()

        return () => headerWidgetSignal.value = undefined;
    }, [data])

    return (<>
        {data &&
            <>
                {!onViewPage && <SidePanelHeader descriptor="Pallet" value={data.reference} statuses={data.status} onClick={(() => { viewEntity() })} />}
                <div className={`display--flex gap--xs flex__wrap--wrap ${onViewPage ? '' : 'margin__top--s'}`}>
                    {hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Move_Outer_Cube) &&
                        <Button isDisabled={globalSubmittingSignal.value.isBusy} className={"button button--small button--primary button--with-icon"} onPress={handleMove}>
                            <img src={moveIcon} />
                            <p className="button-text">Move</p>
                        </Button>
                    }
                    {data?.type?.value === OuterCubeTypeSmartEnum.Uplift.value &&
                        hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Add_Outer_Cube) &&
                        <EditAction id={data.id} displayText="Bulk Update Pallet" entityName={data.entityName} />
                    }
                    {hasPermission(permissionsContext.permissions!, PermissionTypes.read, PermissionConcerns.Outer_Cube_Label) &&
                        <PrintOuterCubeLabel outerCubeId={data.id} />
                    }
                    {hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Operate_Workstation) &&
                        <Button className="button button--secondary button--small" onPress={handlePartFullOrFull}>
                            <p className="button-text">Mark As Part Full/Full</p>
                        </Button>
                    }
                    {hasPermission(permissionsContext.permissions!, PermissionTypes.write, PermissionConcerns.Delete_Outer_Cube) &&
                        <Button isDisabled={globalSubmittingSignal.value.isBusy} className="button button--small button--primary button--secondary--destructive button--with-icon" onPress={handleDeleteOuterCube}>
                            <p className="button-text">Delete</p>
                        </Button>
                    }
                </div>
                <SidePanelInfo values={[
                    { label: "Status", value: onViewPage ? data.status : undefined },
                    { label: "Reference", value: data.reference },
                    { label: "Fullness", value: fullness },
                    { label: "Type", value: getOuterCubeTypeForDisplay() },
                    { label: "Belongs to", value: data.belongsTo }

                ]}
                />
                <SidePanelBreadCrumb descriptor="Current Location" value={location} />
                <SidePanelOuterCubeMaterial data={data} />
                {
                    !onViewPage &&
                    hasPermission(permissionsContext.permissions!, PermissionTypes.read, PermissionConcerns.Inventory_Audit_Trails) &&
                    (<SidePanelAuditTrail entityName={EntityNames.outerCubes} id={data.id} viewFunction={() => viewEntityAndTab(EntityNames.audits)} />)
                }
                {hasPermission(permissionsContext.permissions!, PermissionTypes.read, PermissionConcerns.Non_PII_Notes) &&
                    <SidePanelNonSensitiveInfo descriptor="Notes" data={data} dataTarget="notes" />}
            </>
        }
    </>)
}

export function OuterCubeFullness({ amount }: { amount: number }) {
    return <div className="pallet-stats__widget">
        <Percentage amount={amount} />
    </div>
}